import { addDays, ExerciseList, planText, SplitList, startOfWeek, todayIn } from '@squats/shared';
import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { api } from '../api.ts';
import { useUser } from '../auth.tsx';
import { useCachedApi } from '../cache.ts';
import { dayOf, isPlanner, PlanBadge, togetherText, useCalendar } from '../calendar.tsx';
import { DaySheet } from '../components/DaySheet.tsx';
import { formatDayShort, formatMonthDay, formatWeekday } from '../dates.ts';
import { useApi } from '../useApi.ts';

export function PlanPage() {
  const user = useUser();
  const today = todayIn(user.timezone);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(today));
  const weekEnd = addDays(weekStart, 6);
  const { data: cal, error, reload } = useCalendar(weekStart, weekEnd);
  const { data: splitList } = useApi(() => api('/api/splits', SplitList), []);
  const { data: library } = useCachedApi(user.id, '/api/exercises', ExerciseList);
  const exercises = useMemo(() => new Map((library?.exercises ?? []).map((e) => [e.id, e])), [library]);
  const planner = isPlanner(user);
  // The day sheet open: whose day, and which.
  const [editing, setEditing] = useState<{ userId: string; date: string } | null>(null);

  const dates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const thisWeek = startOfWeek(today);

  return (
    <div className="stack-lg">
      <div className="row">
        <h1 className="grow">Plan</h1>
        {planner && (
          <Link href="/plan/setup" className="btn small">
            Splits & schedule
          </Link>
        )}
      </div>

      <div className="row week-nav">
        <button type="button" className="btn small" onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Previous week">
          ‹
        </button>
        <span className="grow center">
          {formatMonthDay(weekStart)} – {formatMonthDay(weekEnd)}
        </span>
        {weekStart !== thisWeek && (
          <button type="button" className="btn small" onClick={() => setWeekStart(thisWeek)}>
            This week
          </button>
        )}
        <button type="button" className="btn small" onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Next week">
          ›
        </button>
      </div>

      {planner && splitList && splitList.splits.length === 0 && (
        <section className="card stack">
          <h2>Start here</h2>
          <p>Set up your splits (like “Chest & Triceps”) and a repeating schedule, and your weeks fill in.</p>
          <div>
            <Link href="/plan/setup" className="btn primary">
              Set up splits & schedule
            </Link>
          </div>
        </section>
      )}

      {error && !cal && <p className="error">{error}</p>}
      {cal && (
        <ol className="days" aria-label="Days this week">
          {dates.map((date) => (
            <li key={date} className={date === today ? 'day-card today-card' : 'day-card'}>
              <h2 className="day-head">
                {formatWeekday(date, true)} <span className="muted">{formatMonthDay(date)}</span>
                {date === today && <span className="pill ok">Today</span>}
              </h2>
              <ul className="list">
                {cal.users.map((u) => {
                  const d = dayOf(cal, u.id, date)!;
                  const isMe = u.id === user.id;
                  const content = (
                    <>
                      <span className="who">{isMe ? 'You' : u.displayName}</span>
                      <span className="grow">
                        <PlanBadge cal={cal} day={d} />
                      </span>
                      {d.together.length > 0 && <span className="together small">🤝 {togetherText(cal, d)}</span>}
                      {d.exercises.length > 0 && (
                        <span className="planned-names muted small">
                          {d.exercises
                            .map((e) => `${exercises.get(e.exerciseId)?.name ?? 'Exercise'} ${planText(e, exercises.get(e.exerciseId)?.type ?? 'weight_reps', cal.users.find((x) => x.id === u.id)?.units ?? user.units)}`)
                            .join(' · ')}
                          {d.exercisesEdited && ' (this day)'}
                        </span>
                      )}
                    </>
                  );
                  return (
                    <li key={u.id}>
                      {planner ? (
                        <button
                          type="button"
                          className="plan-row mine"
                          onClick={() => setEditing({ userId: u.id, date })}
                          aria-label={`Change ${isMe ? 'your' : `${u.displayName}’s`} plan for ${formatDayShort(date)}`}
                        >
                          {content}
                          <span className="chev" aria-hidden="true">
                            ›
                          </span>
                        </button>
                      ) : (
                        <div className="plan-row">{content}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ol>
      )}

      {cal && editing && (
        <DaySheet
          key={`${editing.userId}-${editing.date}`}
          open
          onClose={() => setEditing(null)}
          onChanged={reload}
          cal={cal}
          editor={user}
          subjectId={editing.userId}
          date={editing.date}
          today={today}
        />
      )}
    </div>
  );
}
